import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, PaperPlaneTilt, Sparkle, CheckCircle, Warning, ArrowsClockwise,
  MagnifyingGlass, ShieldWarning, CaretDown, CaretUp, WarningOctagon, Eye,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// mode = 'check' → only AI audit, hide the Send footer
// mode = 'prepare' → full AI + draft + Send footer
export default function BulkPrepareEAModal({ open, onOpenChange, students, onSent, mode = 'prepare' }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);   // student_id currently rendering a preview
  const [results, setResults] = useState([]);   // [{student_id, draft, compliance, ok, error}]
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]); // rows the staff ticks to include in Send
  const [expandedId, setExpandedId] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [pendingRedIds, setPendingRedIds] = useState([]); // ids that need override on Send
  // Live progress from the check-mode job poller: `{done, total, counts}`.
  const [progress, setProgress] = useState(null);
  // Track the running bulk-check job so the registrar can hit "Cancel"
  // if it stalls. Non-null only while a check is in flight.
  const [activeJobId, setActiveJobId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const isCheckOnly = mode === 'check';

  // For CHECK mode we hit the async job endpoint (kick off → poll →
  // read results). Big cohorts (256+ students) took 5+ min synchronously
  // which blew past Cloudflare's 100s edge timeout and left the tab
  // hanging on a white screen. The job pattern keeps the request short
  // and streams progress via 1.5s polls. PREPARE mode still uses the
  // synchronous endpoint — draft assembly is fast, and prepare batches
  // are almost always <30 rows in practice.
  // Batch size for check-mode. We cap at 50/run because:
  //   * ~50 students × ~10s Gemini = 8-9 min per batch — under the 15 min
  //     Cloudflare edge + backend wall-clock budgets with plenty of slack
  //   * Rate limits at Gemini free tier bite HARD above ~60 concurrent
  //     Gemini calls per minute; 50-at-a-time keeps us comfortably under
  //   * Staff sees results faster and can keep working through cohorts
  //     without being trapped waiting on a 261-student job
  const CHECK_BATCH_SIZE = 50;
  // Ids that have been audited in this modal session — so a "Continue
  // with next 50" click sends only the un-audited ones.
  const [auditedIds, setAuditedIds] = useState(new Set());

  const loadCheckAsJob = async (targetStudents) => {
    const list = targetStudents || students;
    setProgress({ done: 0, total: list.length, counts: { green: 0, yellow: 0, red: 0, errors: 0 } });
    const { data: kicked } = await axios.post(`${API}/institution/ea/bulk-check-jobs`, {
      student_ids: list.map(s => s.id),
      force_refresh: false,
    });
    const jobId = kicked.job_id;
    setActiveJobId(jobId);
    const deadline = Date.now() + 30 * 60 * 1000; // 30 min ceiling — covers ~1000 students on cold cache
    try {
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        try {
          const { data: s } = await axios.get(`${API}/institution/ea/bulk-check-jobs/${jobId}`);
          setProgress({ done: s.done || 0, total: s.total || list.length, counts: s.counts || {} });
          if (s.state === 'done') return s.results || [];
          // "cancelled" is a graceful early-stop (staff hit Cancel or
          // wall-clock ceiling fired). Return whatever partial results
          // the backend flushed rather than throwing.
          if (s.state === 'cancelled') return s.results || [];
          if (s.state === 'failed') throw new Error(s.error || 'Audit job failed');
        } catch (pollErr) {
          // Ignore transient poll errors; keep trying until deadline
          if (pollErr?.message === 'Audit job failed') throw pollErr;
        }
      }
      throw new Error('Audit timed out — please try a smaller batch');
    } finally {
      setActiveJobId(null);
    }
  };

  const handleCancelAudit = async () => {
    if (!activeJobId || cancelling) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/institution/ea/bulk-check-jobs/${activeJobId}/cancel`);
      // The poll loop will pick up state='cancelled' and return partial
      // results within ~1.5s. Don't null-out state here — let the loop do it.
    } catch (err) {
      // Non-fatal — if cancel fails, staff can just close the modal.
    } finally {
      // Reset the flag after a beat so the button doesn't spin forever
      // if the backend was slow to acknowledge.
      setTimeout(() => setCancelling(false), 3000);
    }
  };

  const load = async () => {
    if (!students?.length) return;
    setLoading(true);
    setError('');
    setSelectedIds([]);
    setExpandedId(null);
    setProgress(null);
    setAuditedIds(new Set());
    try {
      if (isCheckOnly) {
        // First batch: take the first 50 students. Staff can hit
        // "Continue with next 50" after this completes to keep going.
        const firstBatch = students.slice(0, CHECK_BATCH_SIZE);
        const results = await loadCheckAsJob(firstBatch);
        setResults(results);
        setAuditedIds(new Set(firstBatch.map(s => s.id)));
      } else {
        const { data } = await axios.post(`${API}/institution/ea/bulk-prepare`, {
          student_ids: students.map(s => s.id),
          force_refresh: false,
        });
        setResults(data.results || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Bulk check failed');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    setResults([]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const counts = useMemo(() => {
    const c = { green: 0, yellow: 0, red: 0, error: 0 };
    for (const r of results) {
      if (!r.ok) { c.error += 1; continue; }
      const overall = (r.compliance?.overall || '').toLowerCase();
      if (overall === 'red') c.red += 1;
      else if (overall === 'yellow') c.yellow += 1;
      else c.green += 1;
    }
    return c;
  }, [results]);

  const toggleSelect = (sid) => {
    setSelectedIds(prev => prev.includes(sid) ? prev.filter(id => id !== sid) : [...prev, sid]);
  };
  const selectableIds = results.filter(r => r.ok && r.draft).map(r => r.student_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableIds);

  const doSend = async (overrideReasonText = '') => {
    const chosen = results.filter(r => r.ok && r.draft && selectedIds.includes(r.student_id));
    if (!chosen.length) return;
    setSending(true);
    setOverrideOpen(false);
    try {
      const { data } = await axios.post(`${API}/institution/ea/bulk-send`, {
        items: chosen.map(r => ({
          student_id: r.student_id,
          draft: r.draft,
          compliance_override_reason: (r.compliance?.overall || '').toLowerCase() === 'red' ? overrideReasonText : '',
        })),
      });
      const sent = data.results?.filter(r => r.ok).length || 0;
      const failed = (data.results?.length || 0) - sent;
      if (sent > 0) toast.success(`${sent} enrollment agreement${sent === 1 ? '' : 's'} sent via DocuSign`);
      if (failed > 0) toast.error(`${failed} failed — check the details in the toast history`);
      onSent?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally {
      setSending(false);
      setOverrideReason('');
      setPendingRedIds([]);
    }
  };

  const handleSendClick = () => {
    const chosenRed = results.filter(r => r.ok && r.draft && selectedIds.includes(r.student_id) && (r.compliance?.overall || '').toLowerCase() === 'red');
    if (chosenRed.length > 0) {
      setPendingRedIds(chosenRed.map(r => r.student_id));
      setOverrideOpen(true);
      return;
    }
    doSend('');
  };

  // Preview the exact PDF that will be DocuSign-sent for a single student.
  // Reuses the /ea/preview.pdf endpoint the single-student modal already
  // uses so staff see the same fidelity render, per row.
  const handlePreview = async (row) => {
    if (!row?.draft) return;
    // Open a blank tab synchronously so popup blockers don't fire — we'll
    // navigate it to the blob URL as soon as the render is back.
    let win = null;
    try {
      win = window.open('', '_blank');
      if (win) {
        win.document.title = `EA Preview — ${row.student_id}`;
        win.document.body.style.cssText = 'font-family:-apple-system,sans-serif;padding:48px;color:#555;';
        win.document.body.innerHTML = '<p>Rendering preview…</p>';
      }
    } catch { /* pop-up blocked — fall through, download instead */ }
    setPreviewingId(row.student_id);
    try {
      const resp = await axios.post(
        `${API}/institution/students/${row.student_id}/ea/preview.pdf`,
        row.draft,
        { responseType: 'blob', timeout: 30000 },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `ea-preview-${row.student_id}.pdf`;
        a.click();
      }
    } catch (e) {
      if (win && !win.closed) win.close();
      let detail = '';
      const body = e?.response?.data;
      if (body instanceof Blob) {
        try {
          const txt = await body.text();
          try { detail = (JSON.parse(txt) || {}).detail || txt; } catch { detail = txt; }
        } catch { /* ignore */ }
      } else if (body?.detail) {
        detail = body.detail;
      }
      toast.error(detail || 'Preview failed');
    } finally {
      setPreviewingId(null);
    }
  };

  if (!open) return null;

  const studentById = (id) => students?.find(s => s.id === id);

  const statusPill = (r) => {
    if (!r.ok) return { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Error', icon: WarningOctagon };
    const overall = (r.compliance?.overall || '').toLowerCase();
    if (overall === 'red') return { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Blocker', icon: WarningOctagon };
    if (overall === 'yellow') return { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Warning', icon: ShieldWarning };
    return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Clean', icon: CheckCircle };
  };

  // Per-requirement pills — mirrors the college's tool: staff scan ID + English
  // at a glance. present → green, missing → red, unknown → gray.
  const reqPill = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'present') return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Present', icon: CheckCircle };
    if (s === 'missing') return { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Missing', icon: WarningOctagon };
    return { cls: 'bg-gray-50 text-gray-500 border-gray-200', label: '—', icon: ShieldWarning };
  };

  const modalNode = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      role="dialog"
      aria-modal="true"
      data-testid="bulk-ea-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-start gap-3 border-b border-gray-200 px-6 py-4">
          <div className={`shrink-0 w-10 h-10 rounded-md flex items-center justify-center ${isCheckOnly ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {isCheckOnly ? <MagnifyingGlass size={20} weight="fill" /> : <Sparkle size={20} weight="fill" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900">
              {isCheckOnly ? 'Bulk Document Check' : 'Bulk Prepare Enrollment Agreements'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isCheckOnly ? (
                (students?.length || 0) > CHECK_BATCH_SIZE ? (
                  <>
                    Auditing in batches of <span className="font-semibold text-gray-700">{CHECK_BATCH_SIZE}</span> to avoid rate limits.{' '}
                    <span className="font-semibold text-gray-700">{Math.min(CHECK_BATCH_SIZE, students.length - auditedIds.size)}</span> of {students.length} this batch · {students.length - auditedIds.size} remaining.
                  </>
                ) : (
                  `Running the AI compliance audit on ${students?.length || 0} student${students?.length === 1 ? '' : 's'}. No EA drafts are created.`
                )
              ) : (
                `Preparing EA drafts for ${students?.length || 0} student${students?.length === 1 ? '' : 's'}. Tick the rows you want to send, then click Send.`
              )}
            </p>
          </div>
          <button onClick={() => onOpenChange(false)} className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center hover:bg-gray-100 text-gray-500" aria-label="Close" data-testid="bulk-ea-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6" data-testid="bulk-ea-body">
          {loading && (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="bulk-ea-loading">
              <div className="inline-flex items-center gap-2">
                <ArrowsClockwise size={16} className="animate-spin" />
                {isCheckOnly ? 'Auditing documents' : 'Analysing documents & preparing drafts'} for {students?.length || 0} student{students?.length === 1 ? '' : 's'}…
              </div>
              {isCheckOnly && progress && progress.total > 0 && (
                <div className="mt-5 max-w-md mx-auto">
                  <div className="flex items-center justify-between text-[11px] font-medium text-gray-600 mb-1.5">
                    <span>Audited</span>
                    <span className="tabular-nums">{progress.done} / {progress.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-indigo-600 transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }}
                    />
                  </div>
                  {progress.counts && (progress.counts.green + progress.counts.yellow + progress.counts.red + progress.counts.errors) > 0 && (
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-[11px] text-gray-600">
                      <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Clean <span className="font-semibold tabular-nums">{progress.counts.green || 0}</span></span>
                      <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Warning <span className="font-semibold tabular-nums">{progress.counts.yellow || 0}</span></span>
                      <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />Blocker <span className="font-semibold tabular-nums">{progress.counts.red || 0}</span></span>
                      {(progress.counts.errors || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-red-700"><WarningOctagon size={11} weight="fill" />{progress.counts.errors} errors</span>
                      )}
                    </div>
                  )}
                  <p className="mt-3 text-[10.5px] text-gray-400">
                    Runs in the background — safe to keep this tab open. Larger cohorts may take a few minutes.
                  </p>
                  {activeJobId && (
                    <button
                      type="button"
                      onClick={handleCancelAudit}
                      disabled={cancelling}
                      className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-medium text-red-700 hover:text-red-900 border border-red-200 hover:border-red-400 hover:bg-red-50 rounded-md px-3 py-1.5 transition disabled:opacity-50"
                      data-testid="bulk-check-cancel-btn"
                      title="Stop the audit here and use whatever results have already come back"
                    >
                      {cancelling ? (
                        <><ArrowsClockwise size={11} className="animate-spin" /> Cancelling…</>
                      ) : (
                        <><WarningOctagon size={11} weight="fill" /> Cancel & finish with what's done</>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {error && !loading && (
            <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
              <Warning size={16} weight="fill" /> {error}
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600" data-testid="bulk-ea-summary">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Clean: <span className="font-semibold tabular-nums">{counts.green}</span></span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Warning: <span className="font-semibold tabular-nums">{counts.yellow}</span></span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Blocker: <span className="font-semibold tabular-nums">{counts.red}</span></span>
                {counts.error > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-700"><WarningOctagon size={12} weight="fill" />Errors: <span className="font-semibold tabular-nums">{counts.error}</span></span>
                )}
                <span className="ml-auto inline-flex items-center gap-1 text-gray-500">
                  <ArrowsClockwise size={12} className="cursor-pointer hover:text-gray-800" onClick={() => load()} title="Re-run" />
                  Re-run
                </span>
              </div>

              {!isCheckOnly && selectableIds.length > 0 && (
                <div className="flex items-center gap-2 text-[11px] text-gray-600 px-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300"
                    data-testid="bulk-ea-select-all"
                  />
                  <span>Select all prepared rows ({selectableIds.length})</span>
                  <span className="ml-auto">Selected: <span className="font-semibold tabular-nums">{selectedIds.length}</span></span>
                </div>
              )}

              <div className="border border-gray-200 rounded-md divide-y divide-gray-100 overflow-hidden">
                {results.map((r) => {
                  const stu = studentById(r.student_id) || {};
                  const d = r.draft || {};
                  const pill = statusPill(r);
                  const idPill = reqPill(r.compliance?.id_status);
                  const engPill = reqPill(r.compliance?.english_status);
                  const isExpanded = expandedId === r.student_id;
                  const issues = r.compliance?.issues || [];
                  const flags = r.compliance?.flags || [];
                  const canSelect = !isCheckOnly && r.ok && r.draft;
                  const isSelected = selectedIds.includes(r.student_id);
                  const eaId = r.compliance?.ea_identification_documents;
                  const eaEng = r.compliance?.ea_proof_of_english;
                  return (
                    <div key={r.student_id} className={`transition-colors ${isSelected ? 'bg-emerald-50/40' : 'bg-white'}`} data-testid={`bulk-ea-row-${r.student_id}`}>
                      <div className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-xs">
                        <div className="col-span-1 flex items-center gap-2">
                          {canSelect ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(r.student_id)}
                              className="rounded border-gray-300"
                              data-testid={`bulk-ea-checkbox-${r.student_id}`}
                            />
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                        </div>
                        <div className="col-span-3 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{stu.full_name || d.full_name || r.student_id.slice(0, 8)}</p>
                          <p className="text-[10px] text-gray-500 truncate">{stu.email || d.email_address || '—'}</p>
                        </div>
                        <div className="col-span-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${idPill.cls}`} data-testid={`bulk-ea-id-${r.student_id}`} title={r.compliance?.id_type_and_name || 'ID status'}>
                            <idPill.icon size={11} weight="fill" /> ID · {idPill.label}
                          </span>
                          {r.compliance?.id_type_and_name && (
                            <p className="text-[9px] text-gray-500 truncate mt-0.5">{r.compliance.id_type_and_name}</p>
                          )}
                        </div>
                        <div className="col-span-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${engPill.cls}`} data-testid={`bulk-ea-eng-${r.student_id}`} title={r.compliance?.english_type || 'English status'}>
                            <engPill.icon size={11} weight="fill" /> English · {engPill.label}
                          </span>
                          {r.compliance?.english_type && (
                            <p className="text-[9px] text-gray-500 truncate mt-0.5">{r.compliance.english_type}</p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wide ${pill.cls}`} data-testid={`bulk-ea-status-${r.student_id}`}>
                            <pill.icon size={10} weight="fill" /> {pill.label}
                          </span>
                        </div>
                        <div className="col-span-1 flex items-center justify-end gap-1.5">
                          {!isCheckOnly && r.ok && r.draft && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-[10px] text-blue-700 hover:text-blue-900 hover:underline disabled:opacity-50 disabled:cursor-wait"
                              onClick={() => handlePreview(r)}
                              disabled={previewingId === r.student_id}
                              data-testid={`bulk-ea-preview-${r.student_id}`}
                              title="Preview the exact EA PDF that will be sent to this student"
                            >
                              <Eye size={11} weight="bold" /> {previewingId === r.student_id ? 'Rendering…' : 'Preview'}
                            </button>
                          )}
                          {(issues.length > 0 || flags.length > 0 || !r.ok) && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-900"
                              onClick={() => setExpandedId(isExpanded ? null : r.student_id)}
                              data-testid={`bulk-ea-expand-${r.student_id}`}
                            >
                              {issues.length + flags.length || 'Details'}
                              {isExpanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
                            </button>
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-3 pt-1 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-700 space-y-2" data-testid={`bulk-ea-details-${r.student_id}`}>
                          {!r.ok && (
                            <div className="text-red-700"><Warning size={12} weight="fill" className="inline mr-1" /> {r.error}</div>
                          )}
                          {r.compliance?.summary && (
                            <p className="italic text-gray-600">{r.compliance.summary}</p>
                          )}
                          {(eaId || eaEng) && (
                            <div className="bg-white rounded border border-gray-200 p-2 space-y-1">
                              <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Values that will appear on the EA</p>
                              {eaId && (
                                <p><span className="text-gray-500">ID field →</span> <span className="font-medium text-gray-900">{eaId}</span></p>
                              )}
                              {eaEng && (
                                <p><span className="text-gray-500">English field →</span> <span className="font-medium text-gray-900">{eaEng}</span></p>
                              )}
                            </div>
                          )}
                          {flags.length > 0 && (
                            <div>
                              <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Flags</p>
                              <ul className="space-y-1">
                                {flags.map((f, idx) => (
                                  <li key={idx} className="flex items-start gap-1.5 text-amber-800"><ShieldWarning size={12} weight="fill" className="mt-0.5 shrink-0" /> {f}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {issues.length > 0 && (
                            <div>
                              <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Issues</p>
                              <ul className="space-y-1">
                                {issues.map((it, idx) => {
                                  const critical = (it.severity || '').toLowerCase() === 'critical';
                                  return (
                                    <li key={idx} className={`flex items-start gap-1.5 ${critical ? 'text-red-700' : 'text-amber-800'}`}>
                                      {critical ? <WarningOctagon size={12} weight="fill" className="mt-0.5 shrink-0" /> : <ShieldWarning size={12} weight="fill" className="mt-0.5 shrink-0" />}
                                      <span><span className="font-semibold uppercase tracking-wide text-[9px] mr-1">{(it.kind || '').replace(/_/g, ' ')}</span>{it.message}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {results.length === 0 && (
                  <div className="px-4 py-6 text-center text-xs text-gray-400 italic">Nothing analysed yet.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isCheckOnly && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-[11px] text-gray-500">
              Only ticked rows will be sent. Blocker rows require an override reason before they can go out.
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending} data-testid="bulk-ea-cancel">Cancel</Button>
              <Button
                onClick={handleSendClick}
                disabled={!selectedIds.length || sending || loading}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="bulk-ea-send-all"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                {sending ? 'Sending…' : `Send EA to ${selectedIds.length} selected`}
              </Button>
            </div>
          </div>
        )}

        {isCheckOnly && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-[11px] text-gray-500">
              {(students?.length || 0) > auditedIds.size ? (
                <>
                  <span className="font-semibold text-gray-700">{auditedIds.size}</span> of {students.length} audited so far · {students.length - auditedIds.size} remaining.
                </>
              ) : (
                <>Read-only audit. Fix flagged issues, then re-open with <span className="font-semibold">Prepare EA</span> to send.</>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(() => {
                const remaining = students.filter(s => !auditedIds.has(s.id));
                if (!remaining.length) return null;
                const batchSize = Math.min(CHECK_BATCH_SIZE, remaining.length);
                return (
                  <Button
                    onClick={async () => {
                      const nextBatch = remaining.slice(0, CHECK_BATCH_SIZE);
                      setLoading(true);
                      setError('');
                      setProgress(null);
                      try {
                        const newResults = await loadCheckAsJob(nextBatch);
                        // Append new results and mark these students as audited.
                        setResults(prev => [...prev, ...newResults]);
                        setAuditedIds(prev => {
                          const next = new Set(prev);
                          nextBatch.forEach(s => next.add(s.id));
                          return next;
                        });
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || 'Batch failed');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="bulk-ea-check-next-batch"
                    className="bg-indigo-700 hover:bg-indigo-800 text-white text-[12px]"
                    title={`Audit the next ${batchSize} students`}
                  >
                    <ArrowsClockwise size={13} className="mr-1.5" /> Continue with next {batchSize}
                  </Button>
                );
              })()}
              {(() => {
                const erroredCount = results.filter(r => !r.ok).length;
                if (!erroredCount) return null;
                return (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      // Rebuild the students prop to include ONLY errored
                      // rows and re-run the audit. Uses the parent's list
                      // to find full student objects since the modal
                      // itself only has ids on `results`.
                      const errIds = new Set(results.filter(r => !r.ok).map(r => r.student_id));
                      const retryList = students.filter(s => errIds.has(s.id));
                      if (!retryList.length) return;
                      // Kick a fresh check with just those students by
                      // clearing state and using the existing loader.
                      setLoading(true);
                      setError('');
                      setProgress(null);
                      try {
                        setProgress({ done: 0, total: retryList.length, counts: { green: 0, yellow: 0, red: 0, errors: 0 } });
                        const { data: kicked } = await axios.post(`${API}/institution/ea/bulk-check-jobs`, {
                          student_ids: retryList.map(s => s.id),
                          force_refresh: true,
                        });
                        setActiveJobId(kicked.job_id);
                        const deadline = Date.now() + 15 * 60 * 1000;
                        while (Date.now() < deadline) {
                          await new Promise(r => setTimeout(r, 1500));
                          const { data: s } = await axios.get(`${API}/institution/ea/bulk-check-jobs/${kicked.job_id}`);
                          setProgress({ done: s.done || 0, total: s.total || retryList.length, counts: s.counts || {} });
                          if (s.state === 'done' || s.state === 'cancelled') {
                            // Merge new results into the existing ones,
                            // replacing the previously-errored rows.
                            const retryMap = new Map((s.results || []).map(r => [r.student_id, r]));
                            setResults(prev => prev.map(r => retryMap.get(r.student_id) || r));
                            break;
                          }
                          if (s.state === 'failed') {
                            toast.error(s.error || 'Retry failed');
                            break;
                          }
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || 'Retry failed');
                      } finally {
                        setLoading(false);
                        setActiveJobId(null);
                        setProgress(null);
                      }
                    }}
                    disabled={loading}
                    data-testid="bulk-ea-retry-errored"
                    className="text-[12px]"
                    title={`Re-run the audit on only the ${erroredCount} students that errored — usually clears out rate-limit blips`}
                  >
                    <ArrowsClockwise size={13} className="mr-1.5" /> Retry {erroredCount} errored
                  </Button>
                );
              })()}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-ea-close-check">Close</Button>
            </div>
          </div>
        )}
      </div>

      {/* Override confirmation dialog for red rows */}
      {overrideOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" data-testid="bulk-ea-override-dialog">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-3">
              <WarningOctagon size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Override AI blocker?</h3>
                <p className="text-xs text-gray-600 mt-1">
                  {pendingRedIds.length} student{pendingRedIds.length === 1 ? '' : 's'} you selected {pendingRedIds.length === 1 ? 'has' : 'have'} an AI-flagged blocker.
                  Sending anyway will be logged against your account.
                </p>
              </div>
            </div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Override reason (required)</label>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              placeholder="e.g. Notice of Decision confirmed verbally with IRB officer — hard copy expected next week."
              className="w-full text-xs border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-red-400"
              data-testid="bulk-ea-override-reason"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setOverrideOpen(false)} data-testid="bulk-ea-override-cancel">Cancel</Button>
              <Button
                onClick={() => doSend(overrideReason.trim())}
                disabled={overrideReason.trim().length < 10}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-ea-override-confirm"
              >
                Override & Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return createPortal(modalNode, document.body);
}
